import { useRef, useState } from 'react';
import {
  Button,
  IconButton,
  Popover,
  Segmented,
  SettingsIcon,
  Select,
} from '@/shared/ui';
import {
  DEFAULT_READING_PREFS,
  TYPEFACE_LABELS,
  type ActiveLineStyle,
  type ReadingPrefs,
  type ReadingTextSize,
  type ReadingTypeface,
} from '@/features/content/services/readingPrefsService';

export type ReadingPreferencesProps = {
  prefs: ReadingPrefs;
  onChange: (prefs: Partial<ReadingPrefs>) => void;
  onReset: () => void;
};

/** The Reading Preferences popover on the Interactive Text header. */
export function ReadingPreferences({ prefs, onChange, onReset }: ReadingPreferencesProps) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  return (
    <span className="menu-anchor">
      <IconButton
        ref={anchorRef}
        label="Reading preferences"
        size="sm"
        variant="ghost"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <SettingsIcon size={16} />
      </IconButton>

      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchorRef}
        placement="bottom-end"
        label="Reading preferences"
        className="reading-prefs"
      >
        <h4 className="reading-prefs__title">Reading preferences</h4>

        <div className="reading-prefs__row">
          <span className="reading-prefs__label">Active line</span>
          <Segmented
            label="Active line"
            size="sm"
            value={prefs.activeLine}
            onChange={(activeLine: ActiveLineStyle) => onChange({ activeLine })}
            options={[
              { value: 'highlight', label: 'Highlight' },
              { value: 'underline', label: 'Underline' },
              { value: 'none', label: 'None' },
            ]}
          />
        </div>

        <div className="reading-prefs__row">
          <span className="reading-prefs__label">Text size</span>
          <Segmented
            label="Text size"
            size="sm"
            value={prefs.textSize}
            onChange={(textSize: ReadingTextSize) => onChange({ textSize })}
            options={[
              { value: 'sm', label: 'A' },
              { value: 'md', label: 'A' },
              { value: 'lg', label: 'A' },
            ]}
          />
        </div>

        <div className="reading-prefs__row">
          <Select
            label="Typeface"
            value={prefs.typeface}
            onChange={(event) => onChange({ typeface: event.target.value as ReadingTypeface })}
          >
            {(Object.keys(TYPEFACE_LABELS) as ReadingTypeface[]).map((face) => (
              <option key={face} value={face}>
                {TYPEFACE_LABELS[face]}
              </option>
            ))}
          </Select>
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={onReset}
          disabled={
            prefs.activeLine === DEFAULT_READING_PREFS.activeLine &&
            prefs.textSize === DEFAULT_READING_PREFS.textSize &&
            prefs.typeface === DEFAULT_READING_PREFS.typeface
          }
        >
          Reset to default
        </Button>
      </Popover>
    </span>
  );
}
